import DOMPurify from "dompurify";
import { Bold, Italic, List, ListOrdered, Redo2, Undo2 } from "lucide-react";
import MarkdownIt from "markdown-it";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import Editor, {
  BtnBold,
  BtnBulletList,
  BtnItalic,
  BtnNumberedList,
  BtnRedo,
  BtnUndo,
  type ContentEditableEvent,
  createButton,
  Separator,
  Toolbar,
} from "react-simple-wysiwyg";
import TurndownService from "turndown";
import { AppConfig } from "@/common/appConfig";
import {
  DictationButton,
  DictationStatusLine,
} from "@/components/ui/DictationButton";
import { DictationMicMenu } from "@/components/ui/DictationButton/DictationMicMenu";
import { useFieldDictation } from "@/hooks/transcriber/useFieldDictation";
import { type CaretTarget, placeCaret } from "@/lib/clickCaret";
import { cn } from "@/lib/utils";
import {
  decorateVerifyInEditor,
  markVerifyValuesForEditor,
  unwrapVerifyDecorations,
} from "@/lib/verifyEditor";
import { selectVerifyMarker } from "@/lib/verifyMarkers";

interface Props {
  initValue: string;
  onChange: (value: string) => void;
  // Transcription language for the mic (input code); defaults to settings.
  dictationLanguage?: string;
  // Select the n-th "[verify]" marker once the note is in the editor (a new
  // `key` selects again), so the clinician lands on the unclear value.
  focusVerifyMarker?: { index: number; key: number } | null;
  // Click-to-edit: put the cursor where the note was clicked.
  focusCaret?: (CaretTarget & { key: number }) | null;
}

const mdParser = new MarkdownIt({
  html: true,
  breaks: true,
  linkify: true,
});

// Kept on the editor's own HTML: the chip's contenteditable=false.
const SANITIZE_EDITOR = { ADD_ATTR: ["contenteditable"] };

// The note HTML for the editor. v3.2: flagged values highlighted and the
// "[verify]" markers as chips (lib/verifyEditor; unwrapped before saving).
const renderForEditor = (
  markdown: string,
  verify: { label: string; title: string } | null,
): string => {
  if (!verify) return DOMPurify.sanitize(mdParser.render(markdown));
  const html = DOMPurify.sanitize(
    mdParser.render(markVerifyValuesForEditor(markdown)),
  );
  const template = document.createElement("template");
  template.innerHTML = html;
  const root = document.createElement("div");
  root.append(template.content);
  decorateVerifyInEditor(root, verify);
  return root.innerHTML;
};

const EditorMarkdown = (props: Props): React.ReactNode => {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const { t, i18n } = useTranslation();
  // Read at render time (stories and tests switch the flag).
  const v3 = AppConfig.enableAppRedesign === true;
  const verifyLabel = t("visit.workspace.verify.chip");
  const verifyTitle = t("visit.workspace.verify.tooltip");
  // v3.2 toolbar: lucide icons, translated names (createButton runs
  // document.execCommand, as the built-in buttons do).
  // biome-ignore lint/correctness/useExhaustiveDependencies: names follow the interface language (t changes with it).
  const buttons = useMemo(
    () => ({
      bold: createButton(t("editorV3.bold"), <Bold />, "bold"),
      italic: createButton(t("editorV3.italic"), <Italic />, "italic"),
      numbered: createButton(
        t("editorV3.numberedList"),
        <ListOrdered />,
        "insertOrderedList",
      ),
      bullets: createButton(
        t("editorV3.bulletList"),
        <List />,
        "insertUnorderedList",
      ),
      undo: createButton(t("editorV3.undo"), <Undo2 />, "undo"),
      redo: createButton(t("editorV3.redo"), <Redo2 />, "redo"),
    }),
    [i18n?.language],
  );
  const editorRef = useRef<HTMLDivElement>(null);
  const statusId = useId();
  // The note's mic (VITE_DICTATION_EVERYWHERE_ENABLED): words go in at the
  // cursor in Edit mode. Nothing opens until the mic is pressed.
  const dictationEnabled = AppConfig.enableDictationEverywhere;
  const getTarget = useCallback(() => editorRef.current, []);
  const dictation = useFieldDictation({
    getTarget,
    language: props.dictationLanguage,
  });
  const listening = dictation.status === "listening";
  const wrapperRef = useRef<HTMLDivElement>(null);
  const focusedKey = useRef<number | null>(null);
  const focusVerifyMarker = props.focusVerifyMarker;
  const focusCaret = props.focusCaret;
  const caretKey = useRef<number | null>(null);

  useEffect(() => {
    const initialize = (): void => {
      setHtmlContent(
        renderForEditor(
          props.initValue,
          v3 ? { label: verifyLabel, title: verifyTitle } : null,
        ),
      );
    };
    initialize();
  }, [props.initValue, v3, verifyLabel, verifyTitle]);

  useEffect(() => {
    if (!focusVerifyMarker || focusedKey.current === focusVerifyMarker.key)
      return;
    const editable = wrapperRef.current?.querySelector<HTMLElement>(
      "[contenteditable=true]",
    );
    if (!editable || !htmlContent) return;
    focusedKey.current = focusVerifyMarker.key;
    selectVerifyMarker(editable, focusVerifyMarker.index);
  }, [focusVerifyMarker, htmlContent]);

  useEffect(() => {
    if (!focusCaret || caretKey.current === focusCaret.key) return;
    const editable = wrapperRef.current?.querySelector<HTMLElement>(
      "[contenteditable=true]",
    );
    if (!editable || !htmlContent) return;
    caretKey.current = focusCaret.key;
    placeCaret(editable, focusCaret);
  }, [focusCaret, htmlContent]);

  const convertToMarkdown = (html: string): string => {
    const turndownService = new TurndownService({
      headingStyle: "atx",
      bulletListMarker: "-",
    });
    const md = turndownService.turndown(html);
    return md;
  };

  return (
    <div
      ref={wrapperRef}
      className={cn(
        v3
          ? "v3-editor w-full font-v3"
          : "w-full max-sm:[&_.rsw-ce]:px-0 max-sm:[&_.rsw-editor]:rounded-none max-sm:[&_.rsw-editor]:border-x-0",
        listening &&
          (v3
            ? "[&_.rsw-editor]:ring-2 [&_.rsw-editor]:ring-v3-rec/40"
            : "[&_.rsw-editor]:ring-2 [&_.rsw-editor]:ring-destructive/40"),
      )}
      data-testid={v3 ? "note-editor-v3" : undefined}
    >
      <Editor
        ref={editorRef}
        value={htmlContent}
        onChange={(event: ContentEditableEvent) => {
          if (v3) {
            // The decorations stay in the editor; the Markdown is built from
            // the HTML without them, so it is what the plain editor saves.
            setHtmlContent(
              DOMPurify.sanitize(event.target.value, SANITIZE_EDITOR),
            );
            const value = DOMPurify.sanitize(
              unwrapVerifyDecorations(event.target.value),
            );
            props.onChange(convertToMarkdown(value));
            return;
          }
          const value = DOMPurify.sanitize(event.target.value);
          setHtmlContent(value);
          const md = convertToMarkdown(value);
          props.onChange(md);
        }}
      >
        <Toolbar>
          {v3 ? (
            <>
              <buttons.bold />
              <buttons.italic />
              <Separator />
              <buttons.numbered />
              <buttons.bullets />
              <Separator />
              <buttons.undo />
              <buttons.redo />
            </>
          ) : (
            <>
              <BtnBold />
              <BtnItalic />
              <BtnNumberedList />
              <BtnBulletList />
              <BtnUndo />
              <BtnRedo />
            </>
          )}
          {dictationEnabled && (
            <span className="ml-auto flex items-center pr-1">
              <DictationButton
                // The toolbar stays light in both themes, like its buttons.
                className={
                  dictation.isActive
                    ? undefined
                    : v3
                      ? "text-v3-text-2 hover:bg-v3-surface-2 hover:text-v3-text"
                      : "text-[#222] hover:bg-[#eaeaea] hover:text-[#222]"
                }
                describedBy={statusId}
                dictation={dictation}
                fieldLabel={t("dictation.field.fields.note")}
              />
              <DictationMicMenu
                className={
                  v3
                    ? "text-v3-text-2 hover:text-v3-text"
                    : "text-[#222] hover:text-[#222]"
                }
                disabled={dictation.isActive}
              />
            </span>
          )}
        </Toolbar>
      </Editor>
      {dictationEnabled && (
        <DictationStatusLine
          className="mt-1 max-sm:px-1"
          dictation={dictation}
          id={statusId}
        />
      )}
    </div>
  );
};

export default EditorMarkdown;
